// src/features/records/components/shared/AttachmentSkeleton.js
import React from 'react';
import PropTypes from 'prop-types';
import styles from './AttachmentSkeleton.module.css';
import AttachmentThumb from './AttachmentThumb';

/**
 * AttachmentSkeleton - Shows loading state for attachments
 * Mirrors the exact structure of RecordAttachments for seamless transition
 * Uses preview data from attachmentsSummary when available
 * Returns null for records without attachments (count is 0, null, or undefined)
 */
const AttachmentSkeleton = ({ previews = [], count = 0, variant = 'grid' }) => {
  // Don't show skeleton for records without attachments
  // Treat null/undefined as 0 (no attachments)
  if (!count || count === 0) {
    return null;
  }
  
  const displayCount = count;
  const maxDisplay = variant === 'compact' ? 3 : displayCount; // Compact shows max 3
  const displayPreviews = previews.slice(0, maxDisplay);
  const remainingCount = displayCount - maxDisplay;
  
  // Transform preview data to attachment format for AttachmentThumb
  const transformPreviewToAttachment = (preview) => {
    const isImage = preview.fileType?.startsWith('image/');
    const isVideo = preview.fileType?.startsWith('video/');
    
    return {
      id: preview.id,
      type: isVideo ? 'video' : (isImage ? 'image' : 'file'),
      storagePath: preview.thumbnail,
      thumbnails: preview.thumbnail ? {
        '400': preview.thumbnail
      } : {},
      metadata: {
        filename: preview.filename || 'Loading...'
      }
    };
  };

  // Grid variant - for RecordDetail view
  if (variant === 'grid') {
    const itemsToShow = displayPreviews.length > 0 ? displayPreviews : 
                        Array.from({ length: Math.min(displayCount, 6) });
    
    return (
      <>
        {itemsToShow.map((preview, index) => {
          const isPlaceholder = !preview;
          const attachment = !isPlaceholder ? transformPreviewToAttachment(preview) : null;
          
          return (
            <div 
              key={preview?.id || `skeleton-${index}`} 
              className={`${styles.attachmentItem} ${styles.skeleton}`}
            >
              {!isPlaceholder && (attachment.type === 'image' || attachment.type === 'video') ? (
                <div className={styles.skeletonWrapper}>
                  <AttachmentThumb attachment={attachment} variant="grid" />
                  <div className={styles.skeletonOverlay} />
                </div>
              ) : (
                <div className={styles.filePlaceholder}>
                  <span className={styles.fileType}>
                    {!isPlaceholder ? attachment.type || 'file' : ''}
                  </span>
                  {!isPlaceholder && attachment.metadata?.filename && (
                    <span className={styles.fileName}>{attachment.metadata.filename}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  }
  
  // Compact variant - for RecordCard view
  const itemsToShow = displayPreviews.length > 0 ? displayPreviews : 
                      Array.from({ length: Math.min(maxDisplay, 3) });
  
  return (
    <div className={styles.recordAttachments}>
      {itemsToShow.map((preview, index) => {
        const isPlaceholder = !preview;
        const attachment = !isPlaceholder ? transformPreviewToAttachment(preview) : null;
        
        return (
          <div 
            key={preview?.id || `skeleton-${index}`} 
            className={`${styles.attachmentPreview} ${styles.skeleton}`}
          >
            {!isPlaceholder && (attachment.type === 'image' || attachment.type === 'video') ? (
              <div className={styles.skeletonWrapper}>
                <AttachmentThumb attachment={attachment} variant="compact" />
                <div className={styles.skeletonOverlay} />
              </div>
            ) : (
              <div className={styles.attachmentPlaceholder}>
                <span>{!isPlaceholder ? attachment.type : ''}</span>
              </div>
            )}
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div className={`${styles.attachmentMore} ${styles.skeleton}`}>
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

AttachmentSkeleton.propTypes = {
  previews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fileType: PropTypes.string,
    thumbnail: PropTypes.string,
    filename: PropTypes.string
  })),
  count: PropTypes.number,
  variant: PropTypes.oneOf(['grid', 'compact'])
};

export default React.memo(AttachmentSkeleton);